/**
 * VideoPlayer — AC4/7/9: full-screen player with resume position, related videos, auth redirect.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Lock } from 'lucide-react';
import { useVideoProgress } from '../hooks/useVideoProgress';
import ThumbnailImg from '../components/knowledge/ThumbnailImg';
import { GlossaryTooltip } from '../components/knowledge/GlossaryTooltip';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8001';

interface Video {
  id: string;
  title: string;
  description?: string;
  video_type: string;
  difficulty?: string;
  topic_tags: string[];
  duration_sec?: number;
  thumbnail_key?: string;
  is_published: boolean;
  is_public: boolean;
}

interface Progress {
  watched_seconds: number;
  completed: boolean;
}

function authHeaders() {
  const token = localStorage.getItem('token') ?? '';
  return { Authorization: `Bearer ${token}` };
}

const formatDuration = (secs?: number) => {
  if (!secs) return '';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const DifficultyBadge: React.FC<{ difficulty?: string }> = ({ difficulty }) => {
  if (!difficulty) return null;
  const colours: Record<string, string> = {
    starter: 'bg-green-500/20 text-green-400',
    intermediate: 'bg-amber-500/20 text-amber-400',
    advanced: 'bg-red-500/20 text-red-400',
  };
  return (
    <span className={`inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-full ${colours[difficulty] ?? 'bg-slate-500/20 text-slate-400'}`}>
      {difficulty}
    </span>
  );
};

export default function VideoPlayer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const token = localStorage.getItem('token');

  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [video, setVideo] = useState<Video | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [related, setRelated] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // AC9: redirect unauthenticated users to login
  useEffect(() => {
    if (!token) {
      navigate(`/login?redirect=/knowledge/${id}`, { replace: true });
    }
  }, [token, id, navigate]);

  // Fetch video metadata, stream URL, and progress
  useEffect(() => {
    if (!id || !token) return;

    async function load() {
      try {
        setLoading(true);
        const [videoRes, streamRes, progressRes] = await Promise.all([
          fetch(`${API_BASE}/api/v1/knowledge/videos`, { headers: authHeaders() }),
          fetch(`${API_BASE}/api/v1/knowledge/videos/${id}/stream-url`, { headers: authHeaders() }),
          fetch(`${API_BASE}/api/v1/knowledge/videos/${id}/progress`, { headers: authHeaders() }),
        ]);

        if (!streamRes.ok) {
          setError('Video not available.');
          return;
        }

        const { url } = await streamRes.json();
        setStreamUrl(url);

        if (videoRes.ok) {
          const { items } = await videoRes.json();
          const found = items.find((v: Video) => v.id === id);
          setVideo(found ?? null);
          // Related videos: same tags, max 5, excluding current
          if (found?.topic_tags?.length > 0) {
            const tag = found.topic_tags[0];
            const related = items
              .filter((v: Video) => v.id !== id && v.topic_tags?.includes(tag))
              .slice(0, 5);
            setRelated(related);
          }
        }

        if (progressRes.ok) {
          setProgress(await progressRes.json());
        }
      } catch {
        setError('Failed to load video.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, token]);

  // AC7: seek to resume position once video metadata is loaded
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid || !progress || progress.watched_seconds === 0) return;

    const handleLoaded = () => {
      if (progress.watched_seconds > 0 && progress.watched_seconds < (vid.duration || Infinity)) {
        vid.currentTime = progress.watched_seconds;
      }
    };

    vid.addEventListener('loadedmetadata', handleLoaded);
    return () => vid.removeEventListener('loadedmetadata', handleLoaded);
  }, [progress, streamUrl]);

  // AC5/6: progress tracking hook
  useVideoProgress(videoRef, id ?? '', video?.duration_sec ?? 0);

  if (!token) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !streamUrl) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-400 gap-4">
        <Lock size={32} />
        <p>{error ?? 'Video not found.'}</p>
        <Link to="/knowledge" className="text-teal-400 hover:underline text-sm">← Back to Knowledge Hub</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/knowledge')}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft size={16} />
            Knowledge Hub
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col lg:flex-row gap-6">
        {/* Main video area */}
        <div className="flex-1 min-w-0">
          {/* Video element */}
          <div className="rounded-xl overflow-hidden bg-black aspect-video">
            <video
              ref={videoRef}
              src={streamUrl}
              controls
              className="w-full h-full"
              playsInline
            />
          </div>

          {/* Video info */}
          {video && (
            <div className="mt-4">
              <div className="flex items-start gap-3 mb-2">
                <h1 className="text-xl font-bold text-white flex-1">{video.title}</h1>
                <DifficultyBadge difficulty={video.difficulty} />
              </div>
              {video.duration_sec && (
                <p className="text-xs text-slate-500 mb-3">{formatDuration(video.duration_sec)}</p>
              )}
              {video.topic_tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {video.topic_tags.map((tag) => (
                    <span key={tag} className="text-[11px] px-2 py-0.5 rounded bg-slate-700/60 text-slate-400">{tag}</span>
                  ))}
                </div>
              )}
              {video.description && (
                <p className="text-sm text-slate-400 leading-relaxed">
                  <GlossaryTooltip>{video.description}</GlossaryTooltip>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Related videos sidebar */}
        {related.length > 0 && (
          <div className="lg:w-72 flex-shrink-0">
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">Related Videos</h2>
            <div className="space-y-3">
              {related.map((v) => (
                <Link
                  key={v.id}
                  to={`/knowledge/${v.id}`}
                  className="flex gap-3 group rounded-lg p-2 hover:bg-slate-800/50 transition-colors"
                >
                  <div className="w-24 h-14 rounded flex-shrink-0 bg-slate-800 flex items-center justify-center overflow-hidden">
                    {v.thumbnail_key ? (
                      /* H1 fix: use signed URL, not raw GCS key */
                      <ThumbnailImg videoId={v.id} alt={v.title} className="w-full h-full object-cover" />
                    ) : (
                      <BookOpen size={16} className="text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white line-clamp-2 group-hover:text-teal-300 transition-colors">{v.title}</p>
                    {v.duration_sec && (
                      <p className="text-[10px] text-slate-500 mt-0.5">{formatDuration(v.duration_sec)}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
